import { AsyncPipe, NgOptimizedImage } from '@angular/common';
import { A11yModule } from '@angular/cdk/a11y';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { filter, Observable, Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AvatarComponent } from '../../components/avatar/avatar.component';
import { EllipsisTooltipDirective } from '../../directives/ellipsis-tooltip.directive';
import { User } from '../../models/user';
import { UserService } from '../../services/user.service';
import { MenuItem } from '../menu/menu.config';
import { UrlPerEnvPipe } from '../menu/pipes/url-per-env.pipe';
import { mobileMenuItems } from './mobile-menu.config';

// DEV-1213: "within the same browser/app session" (spec section 4) — sessionStorage, not the
// desktop rail's localStorage, so the expand state doesn't outlive the tab.
const ADMIN_EXPANDED_KEY = 'mobileMenuAdminExpanded';

@Component({
  selector: 'app-mobile-menu',
  standalone: true,
  imports: [
    A11yModule,
    AsyncPipe,
    AvatarComponent,
    EllipsisTooltipDirective,
    MatIcon,
    NgOptimizedImage,
    RouterLink,
    RouterLinkActive,
    UrlPerEnvPipe
  ],
  templateUrl: './mobile-menu.component.html',
  styleUrl: './mobile-menu.component.scss'
})
export class MobileMenuComponent implements OnInit, OnDestroy {
  @ViewChild('triggerButton') triggerButtonRef?: ElementRef<HTMLButtonElement>;

  readonly items: MenuItem[] = mobileMenuItems;
  userInfo$: Observable<User> | null = null;

  isDrawerOpen = false;
  isAccountSheetOpen = false;
  isAdminExpanded = this.readAdminExpanded();
  isAdminActive = false;

  private subscription = new Subscription();

  constructor(
    private userService: UserService,
    private msalService: MsalService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.userInfo$ = this.userService.getUserInfo();
    this.updateAdminActive(this.router.url);
    this.subscription.add(
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
        this.updateAdminActive(this.router.url);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  openDrawer(): void {
    this.isDrawerOpen = true;
  }

  closeDrawer(): void {
    this.isDrawerOpen = false;
    this.isAccountSheetOpen = false;
    // Focus restoration (spec section 6): return to the trigger that opened the drawer.
    this.triggerButtonRef?.nativeElement.focus();
  }

  onNavigate(): void {
    this.closeDrawer();
  }

  toggleAdmin(): void {
    this.isAdminExpanded = !this.isAdminExpanded;
    sessionStorage.setItem(ADMIN_EXPANDED_KEY, String(this.isAdminExpanded));
  }

  openAccountSheet(): void {
    this.isAccountSheetOpen = true;
  }

  closeAccountSheet(): void {
    this.isAccountSheetOpen = false;
  }

  logout(): void {
    const account = this.msalService.instance.getActiveAccount();
    this.msalService.logoutRedirect({
      account,
      postLogoutRedirectUri: environment.msal.postLogoutUrl
    });
  }

  isItemVisible(item: MenuItem, availableModules: string[]): boolean {
    if (item.children?.length) {
      return item.children.some(child => availableModules.includes(child.claimName));
    }
    return availableModules.includes(item.claimName);
  }

  visibleChildren(item: MenuItem, availableModules: string[]): MenuItem[] {
    return (item.children ?? []).filter(child => availableModules.includes(child.claimName));
  }

  private updateAdminActive(url: string): void {
    const admin = this.items.find(item => item.label === 'Administration');
    this.isAdminActive = !!admin?.children?.some(child => child.path && url.startsWith(`/${child.path}`));
  }

  private readAdminExpanded(): boolean {
    return sessionStorage.getItem(ADMIN_EXPANDED_KEY) === 'true';
  }
}
